import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { Loader2, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';

const InviteStep = ({ setInviteData, handleNext, setError, role }) => {
    const [code, setCode] = useState('');
    const [validating, setValidating] = useState(false);
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        // specific logic to read from sessionStorage
        const storedCode = sessionStorage.getItem('inviteCode');
        if (storedCode) {
            setCode(storedCode);
        }
    }, []);

    const validate = async () => {
        setValidating(true);
        setLocalError('');
        setError('');

        try {
            const validateFn = httpsCallable(functions, 'validateAndClaimInvite');
            const result = await validateFn({ inviteCode: code, role });

            const { data } = result; // { success, invitedBy, code, skipped? }

            if (data.success) {
                setInviteData({
                    inviteCodeUsed: data.code || null,
                    invitedBy: data.invitedBy || null,
                    skipped: data.skipped || false
                });
                handleNext();
            }
        } catch (err) {
            console.error('Invite validation error:', err);
            // Check if it's "not found" or "permission denied" (required)
            const message = err.message || 'Invalid invite code';
            setLocalError(message);
            setError(message);
        } finally {
            setValidating(false);
        }
    };

    const handleSkip = async () => {
        // Try to validate with empty code - backend checks if required
        setValidating(true);
        setLocalError('');
        setError('');

        try {
            const validateFn = httpsCallable(functions, 'validateAndClaimInvite');
            const result = await validateFn({ inviteCode: '', role });

            if (result.data.skipped) {
                setInviteData({
                    inviteCodeUsed: null,
                    invitedBy: null,
                    skipped: true
                });
                handleNext();
            } else {
                setLocalError('Invite code is required.');
                setError('Invite code is required.');
            }
        } catch (err) {
            // If backend throws error for missing code when required
            setLocalError('Invite code is required to sign up.');
            setError('Invite code is required to sign up.');
        } finally {
            setValidating(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: '#1a1a1a' }}>
                    Have an Invite?
                </h3>
                <p className="text-gray-600 font-light">
                    Enter your invite code to join SkillVend.
                </p>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: '#1a1a1a' }}>
                        Invite Code
                    </label>
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black font-mono tracking-wider"
                        style={{ borderColor: '#E8E8E3' }}
                    />
                </div>

                {localError && (
                    <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 p-3 border border-red-100">
                        <AlertCircle className="w-4 h-4" />
                        <span>{localError}</span>
                    </div>
                )}

                <div className="flex flex-col space-y-3 pt-4">
                    <button
                        onClick={validate}
                        disabled={validating || !code}
                        className="w-full py-4 bg-black text-white font-light tracking-widest text-sm transition-all duration-300 hover:bg-opacity-90 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {validating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                        <span>VERIFY CODE</span>
                    </button>

                    <button
                        onClick={handleSkip}
                        disabled={validating}
                        className="w-full py-3 text-gray-500 font-light tracking-wide text-xs hover:text-black transition-colors"
                    >
                        I don't have a code (Skip if allowed)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InviteStep;
